import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/question/forms/shared/Answer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Divider } from '@/components/ui/daisy';
import {
  AnswerMode,
  useAnswerMode,
} from '@/data/hook/question/answers/useAnswerMode';
import { FillInTheBlankOptionCreateDTO } from '@/models/Answer';

type FillInTheBlankOptionItemProps = {
  item: FillInTheBlankOptionCreateDTO;
  totalPositions: number;
  initialEditing?: boolean;
  onUpdateText: (text: string) => void;
  onUpdateMedia: (file: File | string, altText?: string) => void;
  onUpdateValidPositions: (positions: number[]) => void;
  onMarkIncorrect: () => void;
  onDelete: () => void;
};

export default function FillInTheBlankOptionItem({
  item,
  totalPositions,
  initialEditing,
  onUpdateText,
  onUpdateMedia,
  onUpdateValidPositions,
  onMarkIncorrect,
  onDelete,
}: FillInTheBlankOptionItemProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.fillInTheBlankForm',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });

  const {
    mode,
    isEditing,
    handleSwitchToMedia,
    handleSwitchToText,
    handleStartEdit,
    handleFinishEdit,
  } = useAnswerMode({ mediaFile: item.mediaFile, initialEditing });

  const isCorrect = item.validPositions.length > 0;

  return (
    <Answer
      isCorrect={isCorrect}
      isEditing={isEditing}
      isMedia={mode === 'media'}
      className="w-auto min-w-[157px] px-3"
    >
      <AnswerContent
        mode={mode}
        isEditing={isEditing}
        item={item}
        onUpdateMedia={onUpdateMedia}
        onUpdateText={onUpdateText}
        onFinishEdit={handleFinishEdit}
      />
      <Answer.MoreOptions>
        <Answer.MoreOptions.Positions
          totalPositions={totalPositions}
          selectedPositions={item.validPositions}
          onChange={onUpdateValidPositions}
          header={tCommon('validAtBlanks')}
          getPositionLabel={position =>
            t('blankLabel', { number: String(position).padStart(2, '0') })
          }
        />
        <Divider className="my-0 py-4" />
        <Answer.MoreOptions.EditText
          onClick={mode === 'text' ? handleStartEdit : handleSwitchToText}
        />
        <Answer.MoreOptions.EditMedia
          onClick={mode === 'media' ? handleStartEdit : handleSwitchToMedia}
        />
        <ConditionalRenderer condition={isCorrect}>
          <Answer.MoreOptions.MarkIncorrect onClick={onMarkIncorrect} />
        </ConditionalRenderer>
        <Answer.MoreOptions.Delete onClick={onDelete} />
      </Answer.MoreOptions>
    </Answer>
  );
}

type AnswerContentProps = {
  mode: AnswerMode;
  isEditing: boolean;
  onFinishEdit: () => void;
} & Pick<
  FillInTheBlankOptionItemProps,
  'item' | 'onUpdateMedia' | 'onUpdateText'
>;

const AnswerContent = ({
  mode,
  isEditing,
  item,
  onUpdateMedia,
  onUpdateText,
  onFinishEdit,
}: AnswerContentProps) => {
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });

  if (mode === 'media') {
    return (
      <ConditionalRenderer
        condition={isEditing}
        fallback={
          <Answer.MediaPreview
            mediaFile={item.mediaFile}
            altText={item.altText ?? undefined}
            placeholder={tCommon('newOption')}
          />
        }
      >
        <Answer.MediaInput
          initialMediaFile={item.mediaFile}
          initialAltText={item.altText ?? ''}
          onConfirm={(file, altText) => {
            onUpdateMedia(file, altText);
            onFinishEdit();
          }}
          onCancel={onFinishEdit}
        />
      </ConditionalRenderer>
    );
  }

  return (
    <ConditionalRenderer
      condition={isEditing}
      fallback={<Answer.TextPreview text={item.text} />}
    >
      <Answer.TextInput
        initialValue={item.text ?? ''}
        placeholder={tCommon('newOption')}
        onConfirm={value => {
          onUpdateText(value);
          onFinishEdit();
        }}
        onCancel={onFinishEdit}
      />
    </ConditionalRenderer>
  );
};
